import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { ArrowLeft } from "lucide-react";
import { api, qs } from "@/lib/api";
import { formatInt, formatWhen } from "@/lib/format";
import { Empty, PageHeader } from "@/components/PageHeader";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TD, TH, THead, TR } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import type { EventSummary } from "@/features/events/EventsPage";

interface Money {
  cents: number;
  dollars: string;
}

export interface FunnelGroup {
  people: number;
  buyers: number;
  buyers_kept: number;
  conversion_pct: number;
  orders: number;
  unpaid_orders: number;
  payments: number;
  gross_collected: Money;
  refunds: Money;
  net_collected: Money;
}

export interface Funnel {
  event: { id: number; name: string; kind: string; brand_id: number; status: string };
  sessions: { id: number; name: string; starts_at: string; starts_at_mt: string; ends_at: string | null; status: string }[];
  window: { from: string; to: string; from_mt: string; to_mt: string; days: number; complete: boolean };
  products: { any: boolean; ids: number[] };
  cohort: {
    registrations: number;
    seats: number;
    registrants: number;
    repeat_registrations: number;
    guest_seats: number;
    guests_with_contact: number;
    guest_seats_without_contact: number;
    people: number;
    skipped_registrations: number;
    skipped_people: number;
  };
  attended: FunnelGroup;
  registered_not_attended: FunnelGroup;
  total: FunnelGroup;
  excluded: { registration_orders: number; other_product_orders: number; orders_without_items: number };
  definition: string;
  orders: {
    order_id: number;
    contact_id: number;
    name: string;
    role: string;
    attended: boolean;
    placed_at: string;
    placed_at_mt: string;
    status: string;
    products: string;
    payments: number;
    gross_collected: Money;
    refunds: Money;
    net_collected: Money;
  }[];
  orders_truncated: boolean;
  people: { contact_id: number; name: string; role: string; attended: boolean; registration_ids: number[]; bought: boolean; orders: number; net_collected: Money }[];
  people_truncated: boolean;
}

interface EventDates {
  sessions: { id: number; name: string | null; starts_at: string; status: string }[];
}

export interface FunnelSearch {
  event?: number;
  session?: number;
  days?: number;
  product?: string;
}

const DAYS = [7, 14, 30, 60, 90, 180, 365];

/** Events → Sales after an event: what the people registered for a webinar, class or Boot Camp bought afterwards (reports_webinar_funnel). */
export function WebinarFunnelPage() {
  const search = useSearch({ from: "/events/sales" });
  const navigate = useNavigate({ from: "/events/sales" });
  const set = (patch: Partial<FunnelSearch>) => navigate({ search: { ...search, ...patch }, replace: true });
  const [product, setProduct] = useState(search.product ?? "");
  useEffect(() => setProduct(search.product ?? ""), [search.product]);

  const events = useQuery({
    queryKey: ["events", "all-for-sales"],
    queryFn: () => api<{ events: EventSummary[] }>("/api/v1/events" + qs({ when: "all", limit: 500 })),
  });
  const dates = useQuery({
    queryKey: ["event", search.event],
    queryFn: () => api<EventDates>(`/api/v1/events/${search.event}`),
    enabled: !!search.event,
  });
  const report = useQuery({
    queryKey: ["webinar-funnel", search],
    queryFn: () =>
      api<Funnel>(
        "/api/v1/reports/webinar-funnel" +
          qs({ event_id: search.event, session_id: search.session, days: search.days, product_name: search.product, limit: 1000 }),
      ),
    enabled: !!search.event,
    retry: false,
    placeholderData: (p) => p,
  });
  const list = [...(events.data?.events ?? [])].sort((a, b) => (b.last_session_at ?? "").localeCompare(a.last_session_at ?? ""));
  const f = report.data;
  const applyProduct = () => {
    const v = product.trim();
    if (v !== (search.product ?? "")) set({ product: v || undefined });
  };

  return (
    <div className="mx-auto max-w-[1300px] space-y-3">
      <Link to="/events" className="inline-flex items-center gap-1 text-sm text-[var(--color-muted-foreground)] hover:text-[var(--color-foreground)]">
        <ArrowLeft className="size-4" /> Events
      </Link>
      <PageHeader title="Sales after an event" sub="What the people registered for a webinar, class or Boot Camp bought afterwards">
        <div className="flex flex-wrap items-center gap-2">
          <Select
            className="w-auto max-w-[420px]"
            aria-label="Event"
            value={search.event ?? ""}
            onChange={(e) => set({ event: e.target.value ? Number(e.target.value) : undefined, session: undefined })}
          >
            <option value="">Choose an event</option>
            {list.map((e) => (
              <option key={e.id} value={e.id}>
                {e.name}
                {e.last_session_at ? ` (${formatWhen(e.last_session_at)})` : ""}
              </option>
            ))}
          </Select>
          <Select
            className="w-auto"
            aria-label="Date"
            value={search.session ?? ""}
            onChange={(e) => set({ session: e.target.value ? Number(e.target.value) : undefined })}
            disabled={!search.event}
          >
            <option value="">Every date</option>
            {(dates.data?.sessions ?? []).map((s) => (
              <option key={s.id} value={s.id}>
                {s.name ? `${s.name}, ` : ""}
                {formatWhen(s.starts_at)}
                {s.status === "cancelled" ? " (cancelled)" : ""}
              </option>
            ))}
          </Select>
          <Select className="w-auto" aria-label="Days after" value={search.days ?? 30} onChange={(e) => set({ days: Number(e.target.value) === 30 ? undefined : Number(e.target.value) })}>
            {DAYS.map((d) => (
              <option key={d} value={d}>
                {d} days after
              </option>
            ))}
          </Select>
          <Input
            className="w-[300px]"
            aria-label="Products"
            placeholder="Any product (or type part of a name)"
            value={product}
            onChange={(e) => setProduct(e.target.value)}
            onBlur={applyProduct}
            onKeyDown={(e) => e.key === "Enter" && applyProduct()}
          />
        </div>
      </PageHeader>

      {!search.event && <Empty title="Choose an event to see who bought afterwards." />}
      {search.event && report.isLoading && <Skeleton className="h-64" />}
      {search.event && report.error && <Empty title="This report isn't available for that choice.">{report.error.message}</Empty>}
      {search.event && f && <Report f={f} />}
    </div>
  );
}

function Report({ f }: { f: Funnel }) {
  const c = f.cohort;
  const ex = f.excluded;
  const left = [
    ex.registration_orders && `${formatInt(ex.registration_orders)} seat ${ex.registration_orders === 1 ? "order" : "orders"}`,
    ex.other_product_orders && `${formatInt(ex.other_product_orders)} for other products`,
    ex.orders_without_items && `${formatInt(ex.orders_without_items)} with no line items`,
  ].filter(Boolean);
  return (
    <>
      <p className="flex flex-wrap items-center gap-2 text-sm">
        Purchases from {f.window.from_mt} to {f.window.to_mt}
        {f.products.any ? ", any product." : `, ${formatInt(f.products.ids.length)} chosen ${f.products.ids.length === 1 ? "product" : "products"}.`}
        {!f.window.complete && <Badge variant="warning">Window still open: numbers can still grow</Badge>}
      </p>
      <div className="grid gap-3 md:grid-cols-[minmax(0,1fr)_minmax(0,2fr)]">
        <Card>
          <CardHeader>
            <CardTitle>Who registered</CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
            <Stat label="People" value={c.people} />
            <Stat label="Seats" value={c.seats} />
            <Stat label="Registered themselves" value={c.registrants} />
            <Stat label="Repeat registrations" value={c.repeat_registrations} />
            <Stat label="Guests with a contact" value={c.guests_with_contact} />
            <Stat label="Guests with no contact (can't be matched)" value={c.guest_seats_without_contact} />
            <Stat label="Cancelled or waitlisted (left out)" value={c.skipped_registrations} />
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>What they bought</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <THead>
                <TR>
                  <TH />
                  <TH className="text-right">People</TH>
                  <TH className="text-right">Buyers</TH>
                  <TH className="text-right">Conversion</TH>
                  <TH className="text-right">Still kept</TH>
                  <TH className="text-right">Orders</TH>
                  <TH className="text-right">Unpaid</TH>
                  <TH className="text-right">Collected</TH>
                  <TH className="text-right">Refunds</TH>
                  <TH className="text-right">Net</TH>
                </TR>
              </THead>
              <tbody>
                <GroupRow label="Attended" g={f.attended} />
                <GroupRow label="Registered, didn't attend" g={f.registered_not_attended} />
                <GroupRow label="Everyone" g={f.total} strong />
              </tbody>
            </Table>
            {left.length > 0 && <p className="px-3 py-2 text-xs text-[var(--color-muted-foreground)]">Left out: {left.join(", ")}.</p>}
          </CardContent>
        </Card>
      </div>
      <p className="text-xs text-[var(--color-muted-foreground)]">{f.definition}</p>

      <Tabs defaultValue="orders">
        <TabsList>
          <TabsTrigger value="orders">Orders ({formatInt(f.orders.length)})</TabsTrigger>
          <TabsTrigger value="people">People ({formatInt(f.people.length)})</TabsTrigger>
        </TabsList>
        <TabsContent value="orders">
          <Card>
            <CardContent className="p-0">
              {f.orders.length === 0 ? (
                <Empty title="No orders in the window." />
              ) : (
                <Table>
                  <THead>
                    <TR>
                      <TH>Order</TH>
                      <TH>Person</TH>
                      <TH>Attended</TH>
                      <TH>Placed</TH>
                      <TH>Status</TH>
                      <TH>Products</TH>
                      <TH className="text-right">Collected</TH>
                      <TH className="text-right">Refunds</TH>
                      <TH className="text-right">Net</TH>
                    </TR>
                  </THead>
                  <tbody>
                    {f.orders.map((o) => (
                      <TR key={o.order_id}>
                        <TD>
                          <Link to="/billing/orders/$orderId" params={{ orderId: String(o.order_id) }} className="underline-offset-2 hover:underline">
                            {o.order_id}
                          </Link>
                        </TD>
                        <TD>
                          <PersonLink id={o.contact_id} name={o.name} />
                          {o.role === "guest" && <Badge variant="muted" className="ml-1">guest</Badge>}
                        </TD>
                        <TD>{o.attended ? "Yes" : "No"}</TD>
                        <TD className="whitespace-nowrap">{o.placed_at_mt}</TD>
                        <TD>{o.status.replace(/_/g, " ")}</TD>
                        <TD className="max-w-[280px] truncate" title={o.products}>
                          {o.products}
                        </TD>
                        <TD className="text-right tabular-nums">{o.gross_collected.dollars}</TD>
                        <TD className="text-right tabular-nums">{o.refunds.cents ? o.refunds.dollars : ""}</TD>
                        <TD className="text-right tabular-nums font-medium">{o.net_collected.dollars}</TD>
                      </TR>
                    ))}
                  </tbody>
                </Table>
              )}
              {f.orders_truncated && <p className="px-3 py-2 text-xs text-[var(--color-muted-foreground)]">Showing the first {formatInt(f.orders.length)} orders; the totals cover all of them.</p>}
            </CardContent>
          </Card>
        </TabsContent>
        <TabsContent value="people">
          <Card>
            <CardContent className="p-0">
              {f.people.length === 0 ? (
                <Empty title="Nobody registered." />
              ) : (
                <Table>
                  <THead>
                    <TR>
                      <TH>Person</TH>
                      <TH>Came as</TH>
                      <TH>Attended</TH>
                      <TH>Bought</TH>
                      <TH className="text-right">Orders</TH>
                      <TH className="text-right">Net</TH>
                    </TR>
                  </THead>
                  <tbody>
                    {f.people.map((p) => (
                      <TR key={p.contact_id}>
                        <TD>
                          <PersonLink id={p.contact_id} name={p.name} />
                        </TD>
                        <TD>{p.role === "guest" ? "Guest" : p.registration_ids.length > 1 ? `Registered ${p.registration_ids.length} times` : "Registered"}</TD>
                        <TD>{p.attended ? "Yes" : "No"}</TD>
                        <TD>{p.bought ? <Badge variant="success">bought</Badge> : ""}</TD>
                        <TD className="text-right tabular-nums">{p.orders ? formatInt(p.orders) : ""}</TD>
                        <TD className="text-right tabular-nums">{p.orders ? p.net_collected.dollars : ""}</TD>
                      </TR>
                    ))}
                  </tbody>
                </Table>
              )}
              {f.people_truncated && <p className="px-3 py-2 text-xs text-[var(--color-muted-foreground)]">Showing the first {formatInt(f.people.length)} people (buyers first); the totals cover everyone.</p>}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </>
  );
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <>
      <span className="text-[var(--color-muted-foreground)]">{label}</span>
      <span className="text-right tabular-nums">{formatInt(value)}</span>
    </>
  );
}

function GroupRow({ label, g, strong }: { label: string; g: FunnelGroup; strong?: boolean }) {
  return (
    <TR className={strong ? "font-semibold" : undefined}>
      <TD>{label}</TD>
      <TD className="text-right tabular-nums">{formatInt(g.people)}</TD>
      <TD className="text-right tabular-nums">{formatInt(g.buyers)}</TD>
      <TD className="text-right tabular-nums">{g.people ? `${g.conversion_pct}%` : ""}</TD>
      <TD className="text-right tabular-nums">{formatInt(g.buyers_kept)}</TD>
      <TD className="text-right tabular-nums">{formatInt(g.orders)}</TD>
      <TD className="text-right tabular-nums">{g.unpaid_orders ? formatInt(g.unpaid_orders) : ""}</TD>
      <TD className="text-right tabular-nums">{g.gross_collected.dollars}</TD>
      <TD className="text-right tabular-nums">{g.refunds.dollars}</TD>
      <TD className="text-right tabular-nums">{g.net_collected.dollars}</TD>
    </TR>
  );
}

function PersonLink({ id, name }: { id: number; name: string }) {
  return (
    <Link to="/contacts/$contactId" params={{ contactId: String(id) }} className="underline-offset-2 hover:underline">
      {name || `Contact ${id}`}
    </Link>
  );
}
